import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { colors } from '../../styles/GlobalStyles';
import { Loader } from '../../components/Loader';
import { reservationService } from '../../services/reservationService';
import { toast } from 'react-toastify';

const Container = styled.div`
  padding: 20px;
  background: ${colors.background};
  min-height: 100vh;
`;

const Header = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px ${colors.shadow};
  margin-bottom: 30px;
`;

const Title = styled.h1`
  color: ${colors.primary};
  margin-bottom: 10px;
`;

const Subtitle = styled.p`
  color: ${colors.textLight};
  margin-bottom: 20px;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px ${colors.shadow};
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: ${colors.primary};
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  color: ${colors.textLight};
  font-size: 0.9rem;
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px ${colors.shadow};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: ${colors.primary};
  color: white;
`;

const TableHeaderCell = styled.th`
  padding: 15px;
  text-align: left;
  font-weight: 600;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid ${colors.border};
  
  &:hover {
    background: ${colors.background};
  }
`;

const TableCell = styled.td`
  padding: 15px;
  vertical-align: top;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  background: #dc3545;
  color: white;
`;

const MemberInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MemberName = styled.div`
  font-weight: 600;
  color: ${colors.text};
`;

const MemberEmail = styled.div`
  font-size: 0.9rem;
  color: ${colors.textLight};
`;

const ClassInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ClassTitle = styled.div`
  font-weight: 600;
  color: ${colors.text};
`;

const ClassDate = styled.div`
  font-size: 0.9rem;
  color: ${colors.textLight};
`;

const ClassTime = styled.div`
  font-size: 0.9rem;
  color: ${colors.textLight};
`;

const CancelledDate = styled.div`
  font-size: 0.9rem;
  color: ${colors.textLight};
`;

const CancelledReason = styled.div`
  font-size: 0.9rem;
  color: ${colors.textLight};
  font-style: italic;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.textLight};
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 20px;
  opacity: 0.5;
`;

const EmptyTitle = styled.h3`
  margin-bottom: 10px;
  color: ${colors.text};
`;

const EmptyText = styled.p`
  margin-bottom: 0;
`;

// Mobile responsive components
const MobileCard = styled.div`
  display: none;
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 15px;
  box-shadow: 0 2px 8px ${colors.shadow};
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const MobileCardTitle = styled.h3`
  color: ${colors.primary};
  margin: 0;
  font-size: 1.1rem;
`;

const MobileCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const MobileCardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid ${colors.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const MobileCardLabel = styled.span`
  font-weight: 600;
  color: ${colors.text};
  font-size: 0.9rem;
`;

const MobileCardValue = styled.span`
  color: ${colors.textLight};
  font-size: 0.9rem;
  text-align: right;
  flex: 1;
  margin-left: 10px;
`;

const AdminCancelledReservations = () => {
  const [cancelledReservations, setCancelledReservations] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCancelledReservations = async () => {
    try {
      setLoading(true);
      const data = await reservationService.getCancelledReservationsWithDetails();
      setCancelledReservations(data);
    } catch (error) {
      console.error('Error fetching cancelled reservations:', error);
      toast.error('İptal edilen rezervasyonlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCancelledReservations();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <Loader fullScreen />;
  }

  return (
    <Container>
      <Header>
        <Title>🚫 İptal Edilen Rezervasyonlar</Title>
        <Subtitle>
          Üyeler tarafından iptal edilen rezervasyonları görüntüleyin ve yönetin.
        </Subtitle>
      </Header>

      <StatsContainer>
        <StatCard>
          <StatNumber>{cancelledReservations.length}</StatNumber>
          <StatLabel>Toplam İptal Edilen</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>
            {cancelledReservations.filter(r => {
              const cancelledDate = new Date(r.cancelledAt);
              const today = new Date();
              const diffTime = today - cancelledDate;
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              return diffDays <= 7;
            }).length}
          </StatNumber>
          <StatLabel>Son 7 Gün</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>
            {cancelledReservations.filter(r => {
              const cancelledDate = new Date(r.cancelledAt);
              const today = new Date();
              const diffTime = today - cancelledDate;
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              return diffDays <= 30;
            }).length}
          </StatNumber>
          <StatLabel>Son 30 Gün</StatLabel>
        </StatCard>
      </StatsContainer>

      {cancelledReservations.length === 0 ? (
        <EmptyState>
          <EmptyIcon>🚫</EmptyIcon>
          <EmptyTitle>İptal Edilen Rezervasyon Bulunmuyor</EmptyTitle>
          <EmptyText>
            Henüz hiç rezervasyon iptal edilmemiş.
          </EmptyText>
        </EmptyState>
      ) : (
        <>
          {/* Desktop Table */}
          <TableContainer>
            <Table>
              <TableHeader>
                <tr>
                  <TableHeaderCell>Üye</TableHeaderCell>
                  <TableHeaderCell>Ders</TableHeaderCell>
                  <TableHeaderCell>Ders Tarihi</TableHeaderCell>
                  <TableHeaderCell>İptal Tarihi</TableHeaderCell>
                  <TableHeaderCell>İptal Nedeni</TableHeaderCell>
                  <TableHeaderCell>Durum</TableHeaderCell>
                </tr>
              </TableHeader>
              <TableBody>
                {cancelledReservations.map((reservation) => (
                  <TableRow key={reservation.id}>
                    <TableCell>
                      <MemberInfo>
                        <MemberName>
                          {reservation.memberDetails?.firstName} {reservation.memberDetails?.lastName}
                        </MemberName>
                        <MemberEmail>{reservation.memberDetails?.email}</MemberEmail>
                      </MemberInfo>
                    </TableCell>
                    <TableCell>
                      <ClassInfo>
                        <ClassTitle>{reservation.classDetails?.title}</ClassTitle>
                        <ClassTime>
                          {reservation.classDetails?.startTime} - {reservation.classDetails?.endTime}
                        </ClassTime>
                      </ClassInfo>
                    </TableCell>
                    <TableCell>
                      <ClassDate>{formatDate(reservation.classDate)}</ClassDate>
                    </TableCell>
                    <TableCell>
                      <CancelledDate>{formatDateTime(reservation.cancelledAt)}</CancelledDate>
                    </TableCell>
                    <TableCell>
                      <CancelledReason>
                        {reservation.cancelReason || 'Belirtilmemiş'}
                      </CancelledReason>
                    </TableCell>
                    <TableCell>
                      <StatusBadge>İptal Edildi</StatusBadge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Mobile Cards */}
          {cancelledReservations.map((reservation) => (
            <MobileCard key={reservation.id}>
              <MobileCardHeader>
                <MobileCardTitle>
                  {reservation.classDetails?.title}
                </MobileCardTitle>
                <StatusBadge>İptal Edildi</StatusBadge>
              </MobileCardHeader>
              <MobileCardContent>
                <MobileCardRow>
                  <MobileCardLabel>Üye:</MobileCardLabel>
                  <MobileCardValue>
                    {reservation.memberDetails?.firstName} {reservation.memberDetails?.lastName}
                  </MobileCardValue>
                </MobileCardRow>
                <MobileCardRow>
                  <MobileCardLabel>E-posta:</MobileCardLabel>
                  <MobileCardValue>{reservation.memberDetails?.email}</MobileCardValue>
                </MobileCardRow>
                <MobileCardRow>
                  <MobileCardLabel>Ders Tarihi:</MobileCardLabel>
                  <MobileCardValue>{formatDate(reservation.classDate)}</MobileCardValue>
                </MobileCardRow>
                <MobileCardRow>
                  <MobileCardLabel>Saat:</MobileCardLabel>
                  <MobileCardValue>
                    {reservation.classDetails?.startTime} - {reservation.classDetails?.endTime}
                  </MobileCardValue>
                </MobileCardRow>
                <MobileCardRow>
                  <MobileCardLabel>İptal Tarihi:</MobileCardLabel>
                  <MobileCardValue>{formatDateTime(reservation.cancelledAt)}</MobileCardValue>
                </MobileCardRow>
                <MobileCardRow>
                  <MobileCardLabel>İptal Nedeni:</MobileCardLabel>
                  <MobileCardValue>
                    {reservation.cancelReason || 'Belirtilmemiş'}
                  </MobileCardValue>
                </MobileCardRow>
              </MobileCardContent>
            </MobileCard>
          ))}
        </>
      )}
    </Container>
  );
};

export { AdminCancelledReservations };
export default AdminCancelledReservations;
